import { useState } from "react";
import { useSelector } from "react-redux";
import { FiChevronDown, FiCode, FiAlertOctagon } from "react-icons/fi";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  const getOrdinalSuffix = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

function MyWork() {
  const { workItems, workItemsTimeline } = useSelector((state) => state.portfolio);
  const [expandedCard, setExpandedCard] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState([]);

  const toggleCard = (title) => {
    setExpandedCard((prev) => (prev === title ? null : title));
  };

  const toggleSection = (type) => {
    setCollapsedSections((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const timelines = workItemsTimeline || [];
  const works = workItems || [];

  return (
    <section className="mx-auto min-h-[calc(100vh-96px)] max-w-7xl px-5 py-4">
      <h1 className="text-4xl font-bold text-white">My Work</h1>
      <p className="mb-12 mt-3 max-w-2xl text-slate-400 md:mb-16">
        Projects, backend & frontend work, experiments, and selected builds.
      </p>

      <div className="relative ml-2 border-l-2 border-white/10 pb-8 md:ml-6">
        {timelines.map((timelineItem) => {
          const projects = works.filter((w) => w.type === timelineItem.type);

          if (projects.length === 0) return null;

          const isSectionCollapsed = collapsedSections.includes(timelineItem.type);

          return (
            <div key={timelineItem.type} className="relative mb-16 last:mb-0">
              <div className="absolute -left-[17px] top-0 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/10 bg-[#0a0f1c]">
                <div className="h-3 w-3 rounded-full bg-indigo-500 shadow-[0_0_12px_rgba(99,102,241,0.9)]"></div>
              </div>

              <div
                className="group w-fit cursor-pointer pb-8 pl-8 md:pl-12"
                onClick={() => toggleSection(timelineItem.type)}
              >
                <div className="flex items-center gap-4">
                  <h2 className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-3xl font-extrabold tracking-tight text-transparent transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(129,140,248,0.5)]">
                    {timelineItem.type}
                  </h2>
                  <div className={`rounded-full bg-white/5 p-1.5 transition-all duration-300 group-hover:bg-indigo-500/20 ${isSectionCollapsed ? "rotate-0" : "rotate-180"}`}>
                    <FiChevronDown className="h-5 w-5 text-indigo-400" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-400">
                  {formatDate(timelineItem.fromDate)} — {formatDate(timelineItem.toDate)}
                </div>
              </div>

              <div
                className={`grid transition-all duration-500 ease-in-out ${isSectionCollapsed ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
                  }`}
              >
                <div className="overflow-hidden">
                  <div className="flex flex-col gap-8 py-2 pl-8 md:pl-12">
                    {projects.map((work) => {
                      const hasExtras = work.techUsed || work.problemsTackled;
                      const isExpanded = expandedCard === work.title;

                      return (
                        <div key={work.title} className="group relative">
                          <div className="absolute -left-8 top-10 h-[2px] w-8 bg-white/10 transition-colors duration-300 group-hover:bg-indigo-500/50 md:-left-12 md:w-12"></div>

                          <div className="absolute -left-[37px] top-[36px] z-10 h-2.5 w-2.5 rounded-full border-2 border-slate-400 bg-slate-700 transition-colors duration-300 group-hover:border-indigo-400 group-hover:bg-indigo-500 md:-left-[53px]"></div>

                          <div className="flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#0a0f1c]/80 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1.5 hover:border-indigo-500/30 hover:bg-[#111827]/90 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-indigo-500/10">
                            <div className="flex items-center gap-3">
                              <h3 className="text-2xl font-bold tracking-tight text-white">
                                {work.title}
                              </h3>
                            </div>
                            <p className="mt-4 leading-relaxed text-slate-400">
                              {isExpanded || work.description.length <= 190
                                ? work.description
                                : `${work.description.substring(0, 190)}...`}
                            </p>

                            {(hasExtras || work.description.length > 190) && (
                              <button
                                onClick={() => toggleCard(work.title)}
                                className="group/btn mt-6 flex w-full items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3 text-sm font-semibold text-slate-300 transition-all hover:bg-white/[0.06] hover:text-white"
                              >
                                <span>{isExpanded ? "Hide Details" : "View Details"}</span>
                                <div className={`rounded-full bg-black/20 p-1 transition-transform duration-300 ease-in-out ${isExpanded ? "rotate-180" : "rotate-0"}`}>
                                  <FiChevronDown className="h-4 w-4" />
                                </div>
                              </button>
                            )}

                            <div
                              className={`grid transition-all duration-300 ease-in-out ${isExpanded
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                                }`}
                            >
                              <div className="overflow-hidden">
                                <div className="mt-4 flex flex-col gap-6 pt-2">
                                  {work.techUsed && (
                                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-400">
                                        <FiCode className="h-4 w-4" />
                                        Technologies
                                      </div>
                                      <p className="mt-2 font-medium text-slate-200">
                                        {work.techUsed}
                                      </p>
                                      {work.techDesc && (
                                        <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                          {work.techDesc}
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {work.problemsTackled && (
                                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-rose-400">
                                        <FiAlertOctagon className="h-4 w-4" />
                                        Problems Tackled
                                      </div>
                                      <p className="mt-2 font-medium text-slate-200">
                                        {work.problemsTackled}
                                      </p>
                                      {work.probsDesc && (
                                        <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                          {work.probsDesc}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default MyWork;